import { projectAccessFailureMessage, SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";
import type { ProjectAccessVerdict } from "@/api/index.d";
import { NO_PROJECT_CONTACT_GOOGLE_REVIEW_LABEL } from "@/helpers/googleReviewCopy";
import { googleReviewRequestedLabel } from "@/helpers/googleReviewState";
import type { GoogleReviewState } from "@/types/CustomerReview";

import type { ProjectVerdictStatus } from "@/stores/projectVerdicts";
export type { ProjectVerdictStatus } from "@/stores/projectVerdicts";

export const PROJECT_TRIAL_UPSELL_LABEL = "Your free trial has ended. Select a plan to keep using this feature.";

interface ProjectVerdictGateOptions {
	verdictStatus?: ProjectVerdictStatus;
	isAuthenticated: boolean;
	viewOnlyRole?: boolean;
	freeTrialEnded?: boolean;
	verdictFresh?: boolean;
	sharingId?: string | null;
}
export function projectVerdictGates(verdict: ProjectAccessVerdict | undefined, options: ProjectVerdictGateOptions) {
	const verdictStatus = options.verdictStatus ?? (verdict ? "ready" : "unavailable");
	const hasAccess = verdictStatus === "ready" && options.isAuthenticated && verdict?.hasAccess === true;
	const roleBlocksWrite = !!options.viewOnlyRole && (verdict?.origin !== "owner" || verdict.provisional === true);
	const viewOnly = !hasAccess || verdict?.can.editContent !== true || !!options.freeTrialEnded || roleBlocksWrite;
	const writable = hasAccess && !viewOnly;
	// Upsell affordances never grant execution permission or revive a stale verdict.
	const trialOnlyBlocker = hasAccess && verdict?.can.editContent === true && !roleBlocksWrite &&
		options.freeTrialEnded === true && options.verdictFresh !== false;
	const trialUpsell = {
		share: trialOnlyBlocker && verdict?.can.manageAccess === true,
		delete: trialOnlyBlocker && verdict?.can.delete === true,
		requestReview: trialOnlyBlocker && verdict?.can.requestReview === true && !verdict.isExternalEditor
	};
	return {
		verdictStatus,
		trialUpsell,
		hasAccess,
		viewOnly,
		isExternalEditor: writable && verdict?.isExternalEditor === true,
		canEdit: writable,
		canDelete: writable && verdict?.can.delete === true,
		canShare: writable && verdict?.can.manageAccess === true,
		canRequestGoogleReview: writable && verdict?.can.requestReview === true && !verdict.isExternalEditor,
		canRequestEditAccess: verdictStatus === "ready" && !!verdict && viewOnly && (
			!options.isAuthenticated || (verdict.can.requestEditAccess === true && (
				verdict.origin === "share" || verdict.origin === "org-member" || (!!options.sharingId && !verdict.hasAccess)
			))
		),
		canUploadToMediaGrid: writable && verdict?.can.addContent === true
	};
}
export function googleReviewLabel(
	verdict: ProjectAccessVerdict | undefined,
	gates = projectVerdictGates(verdict, { isAuthenticated: true }),
	context: { hasContact?: boolean; alreadyRequested?: boolean; reviewState?: GoogleReviewState } = {}
): string {
	if (gates.verdictStatus === "loading") return "Loading permissions...";
	if (gates.verdictStatus === "unavailable" || !verdict) return projectAccessFailureMessage();
	if (verdict.differentOrg) return "This project belongs to a different organization. You cannot request a review from this organization.";
	if (!gates.canRequestGoogleReview && !gates.trialUpsell.requestReview) {
		return "You do not have permission to request a review for this project.";
	}
	if (context.alreadyRequested)
		return googleReviewRequestedLabel({ hasGoogleReviewRequest: true, googleReviewState: context.reviewState });
	if (context.hasContact === false) return NO_PROJECT_CONTACT_GOOGLE_REVIEW_LABEL;
	if (gates.trialUpsell.requestReview) return PROJECT_TRIAL_UPSELL_LABEL;
	return "Request Google Review";
}

export function projectActionLabel(action: "share" | "delete", gates: Pick<ReturnType<typeof projectVerdictGates>, "verdictStatus" | "canShare" | "canDelete" | "trialUpsell">): string {
	if (gates.verdictStatus === "loading") return "Loading permissions...";
	if (gates.verdictStatus === "unavailable") return projectAccessFailureMessage();
	if (gates.trialUpsell[action]) return PROJECT_TRIAL_UPSELL_LABEL;
	const allowed = action === "share" ? gates.canShare || gates.trialUpsell.share : gates.canDelete || gates.trialUpsell.delete;
	return allowed ? (action === "share" ? "Share this project." : "Delete this project.") :
		(action === "share" ? SHARE_PERMISSION_MESSAGE : "You cannot delete this project.");
}
